'use client';

import { FC } from 'react';
import Image from 'next/image';
import FadeIn from '@/components/FadeIn/FadeIn';
import {
  ProductHero,
  TrustBar,
  ProductFAQ,
  ProductCTAFooter,
} from '../components';

// Feature card component
const FeatureCard: FC<{
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  compactImage?: boolean; // Reduce bottom padding for cards with extra whitespace in image
}> = ({ title, description, image, imageAlt, compactImage }) => (
  <div
    className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 lg:p-10"
    style={compactImage ? { paddingBottom: '24px' } : undefined}
  >
    <div className="mb-6">
      <h3
        className="font-medium text-black mb-3"
        style={{ fontSize: 'clamp(1.25rem, 2vw, 2rem)' }}
      >
        {title}
      </h3>
      <p
        className="text-slate-600"
        style={{ fontSize: 'clamp(1rem, 1.25vw, 1.25rem)' }}
      >
        {description}
      </p>
    </div>
    <div className="rounded-xl overflow-hidden bg-white shadow-sm">
      <Image
        src={image}
        alt={imageAlt}
        width={1200}
        height={600}
        className="w-full h-auto object-contain"
      />
    </div>
  </div>
);

const AI_CHATBOT_FEATURES = {
  teachChatbot: {
    title: 'Teach your chatbot with your content',
    description: 'Upload files and let the AI learn from your docs to give accurate, on-brand answers.',
    image: '/images/products/chatbot/teach-chatbot.svg',
    imageAlt: 'Teach chatbot with content interface',
  },
  starterQuestions: {
    title: 'Engage visitors instantly with starter questions',
    description: 'Greet visitors with preset questions and quick replies to guide them with less effort.',
    image: '/images/products/chatbot/engage-visitors.svg',
    imageAlt: 'Starter questions interface',
  },
  controlVoice: {
    title: 'Control how your chatbot speaks',
    description: 'Choose AI models and set tones like friendly, formal, or engaging to match your brand voice.',
    image: '/images/products/chatbot/control-voice.svg',
    imageAlt: 'Chatbot voice control settings',
  },
  bookMeeting: {
    title: 'Instantly book meeting directly from chatbot on website',
    description: 'Customize colors, theme, and tone so your AI assistant feels like a natural part of your website.',
    image: '/images/products/chatbot/book-meeting.svg',
    imageAlt: 'Book meeting from chatbot interface',
  },
  liveRouting: {
    title: 'Live chat routing to your team',
    description: 'When visitors want to talk, connect them instantly to SDRs on Slack or Teams.',
    image: '/images/products/chatbot/live-routing.svg',
    imageAlt: 'Live chat routing interface',
  },
  videoBot: {
    title: 'Welcome visitors with a video bot',
    description: 'Add a friendly video intro that explains your product and makes your site feel human.',
    image: '/images/products/chatbot/video-bot.svg',
    imageAlt: 'Video bot welcome interface',
  },
};

const AI_CHATBOT_FAQS = [
  {
    question: 'How long does it take to set up the chatbot?',
    answer: 'Setup typically takes less than 15 minutes. Simply upload your content, customize the appearance, and embed the widget on your site.',
  },
  {
    question: 'What kind of data can I use to train the chatbot?',
    answer: 'You can upload PDFs, documents, website URLs, and knowledge base articles. The AI learns from your content to provide accurate, on-brand responses.',
  },
  {
    question: 'Can I make the chatbot match my website design?',
    answer: 'Yes! You can fully customize colors, fonts, positioning, and even add your logo to make the chatbot blend seamlessly with your brand.',
  },
  {
    question: 'Which AI models does the chatbot support?',
    answer: 'We support multiple AI models including GPT-4, GPT-4o-mini, and Claude 3.5 Sonnet. You can choose based on your needs for speed vs. accuracy.',
  },
];

const AIChatbotPage: FC = () => {
  return (
    <div className="bg-white">
      <ProductHero
        iconSrc="/images/products/icon-ai-chatbot.svg"
        label="AI Chatbot"
        title="Chat with every visitor, instantly"
        highlightedWord="instantly"
        subtitle="Engage visitors, answer questions, and capture leads automatically — all in your brand's voice."
        primaryCTA={{
          label: 'Book a demo',
          href: '/book-demo',
        }}
      />

      <TrustBar />

      {/* Custom Feature Section with specific layout */}
      <section className="py-16 md:py-24">
        <div className="container-fluid px-4 md:px-7.5 mx-auto" style={{ maxWidth: 'clamp(1366px, 85vw, 1800px)' }}>
          {/* Section Title */}
          <FadeIn variant="up" delay={0.1}>
            <div className="text-center mb-12">
              <h2
                className="font-medium text-black"
                style={{ fontSize: 'clamp(1.75rem, 3vw, 3rem)' }}
              >
                AI <span style={{ color: '#094FFE' }}>chatbot</span> that works like your team
              </h2>
            </div>
          </FadeIn>

          {/* Features with custom layout */}
          <div className="space-y-6 md:space-y-8">
            {/* Row 1: Full width - Teach your chatbot */}
            <FadeIn variant="up" delay={0.15}>
              <FeatureCard {...AI_CHATBOT_FEATURES.teachChatbot} />
            </FadeIn>

            {/* Row 2: Two columns - Starter questions + Control voice */}
            <FadeIn variant="up" delay={0.2}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-start">
                <FeatureCard {...AI_CHATBOT_FEATURES.starterQuestions} compactImage />
                <FeatureCard {...AI_CHATBOT_FEATURES.controlVoice} />
              </div>
            </FadeIn>

            {/* Row 3: Full width - Book meeting */}
            <FadeIn variant="up" delay={0.25}>
              <FeatureCard {...AI_CHATBOT_FEATURES.bookMeeting} />
            </FadeIn>

            {/* Row 4: Two columns - Live routing + Video bot */}
            <FadeIn variant="up" delay={0.3}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-start">
                <FeatureCard {...AI_CHATBOT_FEATURES.liveRouting} compactImage />
                <FeatureCard {...AI_CHATBOT_FEATURES.videoBot} />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <ProductFAQ faqs={AI_CHATBOT_FAQS} />

      <ProductCTAFooter
        productName="AI Chatbot"
        title="Smarter sales. Faster results."
        subtitle="Powered by marketbetter"
      />
    </div>
  );
};

export default AIChatbotPage;
